/**
 * The picture-in-picture frame's geometry (boxed's `pipGestures.ts` and the constants of
 * `usePipFrame.ts` and `TwitchPipPlayer.tsx`): its layout, the bounds it moves and resizes in, and
 * the math of a drag, a corner resize and a pinch. Pure, so it is tested without a pointer.
 */

/** A viewport point, or an offset, in CSS pixels. */
export interface PipPoint {
  x: number;
  y: number;
}

/**
 * Where the frame sits and how wide it is, which the app keeps per tab: `offset` from where its
 * parent places it, `width` null until the shopper resizes it (it fills its parent's width).
 */
export interface LiveBreakPipLayout {
  offset: PipPoint;
  width: number | null;
}

/** Where the frame opens: in place, at its parent's width. Opening from the widget resets to it. */
export const LIVE_BREAK_PIP_INITIAL_LAYOUT: LiveBreakPipLayout = {
  offset: { x: 0, y: 0 },
  width: null,
};

/** The gap the frame keeps from every edge of the viewport. */
export const PIP_VIEWPORT_MARGIN_PX = 8;
/** Twitch's player minimum (dev.twitch.tv/docs/embed): 400×300. */
export const TWITCH_PLAYER_MIN_WIDTH_PX = 400;
export const TWITCH_PLAYER_MIN_HEIGHT_PX = 300;
/** The narrowest frame a mouse resizes to: Twitch's minimum width. */
export const PIP_MIN_WIDTH_PX = TWITCH_PLAYER_MIN_WIDTH_PX;
/** The narrowest frame on a touch screen; under Twitch's minimum the player scales down. */
export const PIP_PHONE_MIN_WIDTH_PX = 275;
export const PIP_MAX_WIDTH_PX = 1280;
/**
 * The narrowest 16:9 player that meets Twitch's 400×300 minimum (533⅓ wide): a frame narrower than
 * this lays the player out at this width and scales it to fit.
 */
export const PIP_SCALED_PLAYER_WIDTH_PX = Math.max(
  TWITCH_PLAYER_MIN_WIDTH_PX,
  (TWITCH_PLAYER_MIN_HEIGHT_PX * 16) / 9,
);
/** A press that moves less than this is a tap, not a drag. */
export const PIP_TAP_SLOP_PX = 4;

/** A resize corner: top-left, top-right, bottom-left, bottom-right. */
export type PipCorner = "tl" | "tr" | "bl" | "br";
export const PIP_CORNERS: readonly PipCorner[] = ["tl", "tr", "bl", "br"];

/** Each corner's cursor while it can grow and shrink, only grow, or only shrink. */
const CORNER_CURSORS: Record<PipCorner, { both: string; expand: string; shrink: string }> = {
  tl: { both: "nwse-resize", expand: "nw-resize", shrink: "se-resize" },
  tr: { both: "nesw-resize", expand: "ne-resize", shrink: "sw-resize" },
  bl: { both: "nesw-resize", expand: "sw-resize", shrink: "ne-resize" },
  br: { both: "nwse-resize", expand: "se-resize", shrink: "nw-resize" },
};

/** Straight-line distance between two points. */
export function distance(a: PipPoint, b: PipPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** The point halfway between two points. */
export function midpoint(a: PipPoint, b: PipPoint): PipPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** `value` held within `min` and `max`; `max` wins if they cross. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Whether a pointer moved less than the tap slop. */
export function isTap(start: PipPoint, end: PipPoint): boolean {
  return distance(start, end) < PIP_TAP_SLOP_PX;
}

/** The frame width for a pinch: the start width scaled by the fingers' spread, within `bounds`. */
export function pinchWidth(
  startWidth: number,
  startDistance: number,
  currentDistance: number,
  bounds: { min: number; max: number },
): number {
  if (startDistance <= 0) return startWidth;
  return clamp((startWidth * currentDistance) / startDistance, bounds.min, bounds.max);
}

/**
 * The offset that keeps `focus` on the same screen spot while a frame anchored at its bottom-left
 * corner (which grows up and to the right) changes width.
 */
export function anchorOffsetForWidth({
  offset,
  rectLeft,
  rectBottom,
  focus,
  startWidth,
  nextWidth,
}: {
  offset: PipPoint;
  rectLeft: number;
  rectBottom: number;
  focus: PipPoint;
  startWidth: number;
  nextWidth: number;
}): PipPoint {
  const grow = nextWidth / startWidth - 1;
  return {
    x: offset.x - (focus.x - rectLeft) * grow,
    y: offset.y + (rectBottom - focus.y) * grow,
  };
}

/**
 * The width range given the room to the right and the room above for a 16:9 height: never wider
 * than either allows or than `PIP_MAX_WIDTH_PX`, and never narrower than `minWidth` unless the
 * room itself is.
 */
export function widthBounds(
  roomX: number,
  roomY: number,
  minWidth: number,
): { min: number; max: number } {
  const max = Math.min(PIP_MAX_WIDTH_PX, roomX, (roomY * 16) / 9);
  return { min: Math.min(minWidth, max), max };
}

/** How far a drag that wants `desired` (the frame's top-left) may go: inside the viewport's margins. */
export function clampToViewport({
  desired,
  size,
  viewport,
  inset,
}: {
  desired: PipPoint;
  size: { width: number; height: number };
  viewport: { width: number; height: number };
  /** The safe-area insets. */
  inset: { top: number; right: number; bottom: number; left: number };
}): PipPoint {
  const m = PIP_VIEWPORT_MARGIN_PX;
  return {
    x: clamp(desired.x, m + inset.left, viewport.width - size.width - m - inset.right),
    y: clamp(desired.y, m + inset.top, viewport.height - size.height - m - inset.bottom),
  };
}

/** How much a frame `boxWidth` wide scales the player: null at or above the scaled width. */
export function playerScale(boxWidth: number): number | null {
  return boxWidth > 0 && boxWidth < PIP_SCALED_PLAYER_WIDTH_PX
    ? boxWidth / PIP_SCALED_PLAYER_WIDTH_PX
    : null;
}

/** A corner's cursor, from whether the frame can grow (room up and right) and shrink. */
export function cornerCursor(corner: PipCorner, canExpand: boolean, canShrink: boolean): string {
  const cursors = CORNER_CURSORS[corner];
  if (canExpand && canShrink) return cursors.both;
  if (canExpand) return cursors.expand;
  if (canShrink) return cursors.shrink;
  return "default";
}

/**
 * A corner drag's width: the frame changes width alone (the player's 16:9 sets its height), so
 * both axes fold into one change, grown by moving away from the opposite corner.
 */
export function resizeWidth({
  corner,
  startWidth,
  start,
  now,
  bounds,
}: {
  corner: PipCorner;
  startWidth: number;
  start: PipPoint;
  now: PipPoint;
  bounds: { min: number; max: number };
}): number {
  const widthSign = corner === "tr" || corner === "br" ? 1 : -1;
  const heightSign = corner === "bl" || corner === "br" ? 1 : -1;
  const grow = widthSign * (now.x - start.x) + heightSign * (now.y - start.y);
  return clamp(startWidth + grow, bounds.min, bounds.max);
}
